import { useMutation, useQueryClient } from '@tanstack/react-query'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import {
  EmailCompose,
  type ComposeContact,
  type ComposeDraft,
  type ComposeFile,
} from '../email/EmailCompose.js'

export interface SendAddressee {
  /** Job contacts that have an email, in the order they sit on the job. */
  recipients: ComposeContact[]
  /**
   * Who to preselect: the billing contact's address, or the client's own
   * when nobody on the job is marked. Decision 84.
   */
  fallbackTo: string | null
  jobId?: string
  files?: ComposeFile[]
}

/**
 * Emailing a quote or an invoice.
 *
 * The same compose window as every other email. The send is still the
 * document procedure: it renders the PDF, records the version, and on a
 * quote mints the accept link. The PDF is the locked attachment.
 */
export function SendDocumentPopup({
  kind,
  id,
  number,
  addressee,
  open,
  onClose,
}: {
  kind: 'quote' | 'invoice'
  id: string
  number: string
  addressee: SendAddressee
  open: boolean
  onClose: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const sendQuote = useMutation(trpc.quotes.send.mutationOptions())
  const sendInvoice = useMutation(trpc.invoices.send.mutationOptions())
  const send = kind === 'quote' ? sendQuote : sendInvoice
  const label = kind === 'quote' ? 'quote' : 'invoice'

  const submit = async (draft: ComposeDraft) => {
    await send.mutateAsync({
      id,
      to: draft.to[0] ?? null,
      message: draft.body === '' ? null : draft.body,
      ...(draft.subject ? { subject: draft.subject } : {}),
      ...(draft.cc.length > 0 ? { cc: draft.cc } : {}),
      ...(draft.bcc.length > 0 ? { bcc: draft.bcc } : {}),
      ...(draft.attachmentIds.length > 0 ? { attachmentIds: draft.attachmentIds } : {}),
    })
    void queryClient.invalidateQueries(trpc.quotes.pathFilter())
    void queryClient.invalidateQueries(trpc.invoices.pathFilter())
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
  }

  return (
    <EmailCompose
      open={open}
      onClose={onClose}
      title={kind === 'quote' ? 'Send quote' : 'Email invoice'}
      sendLabel={kind === 'quote' ? 'Send quote' : 'Send invoice'}
      contacts={addressee.recipients}
      files={addressee.files ?? []}
      jobId={addressee.jobId}
      document={{ kind, id }}
      allowBlankTo
      subjectOptional
      bodyOptional
      initial={{
        to: addressee.fallbackTo ? [addressee.fallbackTo] : [],
        subject: `${label[0]!.toUpperCase()}${label.slice(1)} ${number}`,
      }}
      locked={[{ name: `${number}.pdf`, detail: 'Attached when you send' }]}
      onSend={async (draft) => {
        try {
          await submit(draft)
        } catch (error) {
          throw new Error(errorMessage(error))
        }
      }}
    />
  )
}
